import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { shareReplay } from 'rxjs/operators';
import { PermissionIdCode } from '@app/shared/models/permission-id-code';
import { NullableDate, NullableString } from '@app/shared/models/nullable';
import { MunicipalityPermissionsMappings } from './municipality.service';
import { FormGroup } from '@angular/forms';
import { RestrictionAction } from '@app/municipality-management/services/municipality-management-queue-state.service';
import { PermissionStatus, PermissionStatusCode } from '@app/municipality-management/models/externe-apps';
import { PermissionRequestPayload } from '@app/municipality-management/models/municipality-management';
import { PermissionCode } from '@app/shared/enums';
import * as initalPermissionList from './../../../../assets/json/initial-default-permission-list.json';

@Injectable({
  providedIn: 'root',
})
export class PermissionService extends ApiService {
  public selectedPermission$: BehaviorSubject<PermissionIdCode> = new BehaviorSubject<PermissionIdCode>({} as PermissionIdCode);
  private appList: PermissionDashboardPage[] = [];
  constructor(public http: HttpClient) {
    super(http);
  }

  baseUri = 'socrates/authz/api/permissions';
  featureUri = 'socrates/authz/api/features';
  assignRestrictionsUri = 'socrates/authz/api/municipalities/assignRestrictions';
  municipalityUri = 'socrates/authz/api/municipalities';

  public getAll(): Observable<Permission[]> {
    return this.http.get<Permission[]>(this.endpoint(this.baseUri, `?$filter=isGenericLink eq true`));
  }

  public getByCode(code: string): Observable<Permission[]> {
    return this.http.get<Permission[]>(this.endpoint(this.baseUri, `?$filter=code eq '${code}'`));
  }

  public getPagePermissons(): Observable<Permission[]> {
    return this.http.get<Permission[]>(this.endpoint(this.baseUri, `?$filter=featureFunction eq 'PAGE' AND featureType ne 'E'`)).pipe(shareReplay());
  }

  public getAllRestrictions(): Observable<FeaturePermission[]> {
    return this.http
      .get<FeaturePermission[]>(
        this.endpoint(
          this.baseUri,
          `?$filter=featureType eq 'E' &$expand=MunicipalityRestrictions($select=guid,isTeaser,municipalityId,creationDate,modifyDate,modifyUser,permissionId;$filter=isDeleted eq false)&$select=guid,germanName`
        )
      )
      .pipe(shareReplay());
  }

  public getAllBasicPremiumRestrictions(): Observable<FeaturePermission[]> {
    return this.http
      .get<FeaturePermission[]>(
        this.endpoint(
          this.baseUri,
          `?$filter=featureFunction eq 'PAGE' AND featureType ne 'E'&$expand=MunicipalityRestrictions($select=guid,isTeaser,isDeleted,municipalityId,creationDate,modifyDate,modifyUser,permissionId;$filter=isDeleted eq false)&$select=guid,germanName,isPremium,isActive,code,name`
        )
      )
      .pipe(shareReplay());
  }

  public getAllRestrictionsByPermissionId(permissionId: string): Observable<FeaturePermission[]> {
    let options = `?$filter=featureType eq 'E' AND guid eq ${permissionId}`;
    options += `&$select=guid,name,isActive,germanName,iconName,isGenericLink,genericExternalURL,creationDate,modifyDate,modifyUser,featureFunction,code,isTokenRequired`;
    return this.http
      .get<FeaturePermission[]>(
        this.endpoint(
          this.baseUri,
          options +
            `&$expand=MunicipalityRestrictions($select=guid,isTeaser,municipalityId,creationDate,modifyDate,modifyUser,permissionId;$filter=isDeleted eq false)`
        )
      )
      .pipe(shareReplay());
  }

  public getFeaturePermissons(municipalityGuid?: string): Observable<FeaturePermission[]> {
    let options = `?$filter=featureType eq 'E'`;
    options += `&$select=guid,name,isActive,germanName,iconName,isGenericLink,genericExternalURL,creationDate,modifyDate,modifyUser,featureFunction,code,isTokenRequired`;
    if (municipalityGuid) {
      options += `&$expand=MunicipalityRestrictions($filter=isDeleted eq false and municipalityId eq ${municipalityGuid}), MunicipalityPermissionsMappings($filter=municipalityId eq ${municipalityGuid};$select=guid,applicationURL)`;
    }
    return this.http.get<FeaturePermission[]>(this.endpoint(this.baseUri, options)).pipe(shareReplay());
  }

  public getFeaturePermissonsAllMunicipalities(): Observable<FeaturePermission[]> {
    let options = `?$filter=featureType eq 'E'`;
    options += `&$select=guid,name,isActive,germanName,iconName,isGenericLink,genericExternalURL,creationDate,modifyDate,modifyUser,featureFunction,code,isTokenRequired`;
    options += `&$expand=MunicipalityRestrictions($select=municipalityId,isTeaser,isDeleted,modifyDate,modifyUser)`;

    return this.http.get<FeaturePermission[]>(this.endpoint(this.baseUri, options)).pipe(shareReplay());
  }

  public initialPageList(tenant: string): PermissionDashboardPage[] {
    const originalInitalPermissionListString = JSON.stringify(initalPermissionList);
    const updatedInitialPermissionListString = originalInitalPermissionListString.replace(/@tenant/g, tenant);
    const updatedInitialPermissionListObj = JSON.parse(updatedInitialPermissionListString);

    this.appList = updatedInitialPermissionListObj.appList;

    return this.appList;
  }

  public getActiveGridInfraModules(municipalityGuid: string): Observable<Permission[]> {
    const filterQuery = `$filter=featureArea eq '${PermissionCode.GRID_INFRA}' and code ne '${PermissionCode.GRID_INFRA}' and featureFunction eq 'PAGE'`;
    const municipalityRestrictionQuery = `(municipalityRestrictions/any(mr: mr/municipalityId eq ${municipalityGuid} and mr/isDeleted eq true) or not municipalityRestrictions/any(mr: mr/municipalityId eq ${municipalityGuid}))`;
    const selectQuery = `$select=code,name`;
    const query = `${filterQuery} and ${municipalityRestrictionQuery}&${selectQuery}`;
    return this.http.get<Permission[]>(this.endpoint(this.baseUri, `?${query}`));
  }

  public createFeature(payload: FeaturePayload): Observable<FeaturePermission> {
    return this.http.post<FeaturePermission>(this.endpoint(this.featureUri), payload);
  }

  public updateFeature(feature: FeaturePermission, payload: any): Observable<Permission> {
    return this.http.patch<Permission>(this.endpoint(this.featureUri, feature.guid), payload);
  }

  public deleteFeature(feature: FeaturePermission): Observable<Permission> {
    return this.http.delete<Permission>(this.endpoint(this.featureUri, feature.guid));
  }

  public assignRestriction(feature: FeaturePermission, action: RestrictionAction, userGuid: string): Observable<any> {
    return this.http.put(this.endpoint(this.assignRestrictionsUri), { permissionID: feature.guid, action: action, modifyUser: userGuid });
  }

  public assignRestrictions(permissions: PermissionRequestPayload[], municipalityKey: NullableString, userGuid: string): Observable<any> {
    return this.http.put(this.endpoint(this.assignRestrictionsUri), { permissions: permissions, municipalityKey, modifyUser: userGuid });
  }

  public addIndividualLink(municipalityId: string, permissionId: string, link: string, userGuid: string): Observable<any> {
    return this.http.put(this.endpoint(this.municipalityUri, municipalityId, 'permissions'), {
      applicationURL: link,
      permissionId: permissionId,
      municipalityId: municipalityId,
      modifyUser: userGuid,
    });
  }

  public updateIndividualLink(municipalityId: string, permissionId: string, link: string, userGuid: string): Observable<any> {
    return this.http.patch(this.endpoint(this.municipalityUri, municipalityId, 'permissions', permissionId), {
      applicationURL: link,
      permissionId: permissionId,
      municipalityId: municipalityId,
      modifyUser: userGuid,
    });
  }
}

export interface Permission extends Restriction {
  guid: string;
  name: string;
  description: string;
  code: string;
  featureArea: string;
  featureFunction: string;
  permission: string;
  isPremium: boolean;
  isActive?: boolean;
  isTeaser?: boolean;
  linkType?: string;
  link?: string;
  allowLinkUpdate?: boolean;
  municipalityPermissionsMappings: MunicipalityPermissionsMappings[];
  parentPermissionID?: string;
  childPermissions: Permission[];
}

export type FeaturePermission = Permission & {
  germanName: string;
  iconName: string;
  isGenericLink: boolean;
  genericExternalURL: string;
  creationUser: string;
  creationDate: Date;
  modifyDate: NullableDate;
  modifyUser: NullableString;
  municipalityRestrictions: MunicipalityRestrictionsMapping[];
  municipalityPermissionsMappings: MunicipalityPermissionsMappings[];
  formGroup: FormGroup;
  status: PermissionStatusCode;
  isDropdownDisabled?: boolean;
  externeAppsStatus?: PermissionStatus[];
  previousStatusCode: PermissionStatusCode;
  featureType: string;
  isTokenRequired: boolean;
  showTokenRequired: boolean;
};

export interface MunicipalityRestrictionsMapping {
  guid: string;
  isTeaser: boolean;
  isDeleted?: boolean;
  municipalityId?: string;
  creationUser: string;
  creationDate: Date;
  modifyUser: string;
  modifyDate: NullableDate;
}

export interface FeaturePayload {
  name?: string;
  germanName: string;
  iconName: string;
  featureFunction: string;
  modifyUser: string;
  genericExternalURL: string;
  isGenericLink: boolean;
  isTokenRequired: boolean;
}

export interface Restriction {
  isRestricted?: boolean;
  isTeaserDisabled?: boolean | false;
}

export interface PermissionDashboardPage extends Permission {
  isActive: boolean;
  guid: string;
  name: string;
  description: string;
  code: string;
  featureArea: string;
  featureFunction: string;
  featureType: string;
  permission: string;
  isPremium: boolean;
  isTeaser: boolean;
  isAnchor: boolean;
  isRestricted?: boolean;
  hoverSrc: string;
  src: string;
  class: string;
  title: string;
  clickTrackName: string;
  routerLinkValue: string;
  badgeCount: string;
  hasNotification: boolean;
  isWarmeDaten: boolean;
  isNewTeaserText: boolean;
  isTokenRequired?: boolean;
}
